import { Col, Container, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import { axiosPrivate } from "../api/axios";
import { useParams } from "react-router-dom";
import { Button, CircularProgress } from "@mui/material";
import { useShoppingCart } from "../context/ShoppingCartContext";
import DocumentUpload from "../components/DocumentUpload"
import { formatCurrency } from "../functions/formatCurrency";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "../assets/styles/pages/_order.scss";

dayjs.extend(utc);
dayjs.extend(timezone);


export default function Order({ reorder }) {
  const [isLoading, setIsLoading] = useState(true);
  const [order, setOrder] = useState([]);
  const { seqno } = useParams();
  let { cartQuantity, setCartItems } = useShoppingCart();

  const url = '/api/exo/orders/' + seqno + '/list';

  useEffect(() => {
    (async () => {
      await axiosPrivate.get(url)
        .then((response) => {
          setOrder(response.data.data)
        })
        .catch((error) => {
          if (error.response) {
            console.log(error.response.data); // => the response payload
          }
        })
        .finally(() => setIsLoading(false))
    })();
  }, [setOrder, setIsLoading, url])

  let bg_color = "#f7f9ff";
  if (reorder) {
    bg_color = '#f9fff9';
  }

  function handleReorder() {
    if (cartQuantity > 0) {
      if (window.confirm('You already have items in your cart. Click ok to clear current cart?')) {
        setCartItems([])
      }
    }

    alert('Trigger a re-purchase on order: ' + seqno);
  }

  // Calculate rebate amount
  const calculateRebateAmount = () => {
    if (!order.LINES) return 0;

    const stcItems = order.LINES.filter(line => line.STOCKCODE === 'WS-H-STC-ASSIGNED');
    return stcItems.reduce((total, item) => {
      return total + (Math.floor(item.ORDQUANT) * 35); // $35 per STC certificate
    }, 0);
  };

  const rebateAmount = calculateRebateAmount();
  const finalTotal = order.ORDTOTAL ? order.ORDTOTAL - rebateAmount : 0;

  return (
    <Container>
      <div className="mb-3">
        <div className="page-title-wrapper">
          <h1 className="page-title">
            <span className="base"
              data-ui-id="page-title-wrapper">{reorder && "Re-"}Order{reorder && "ing"} #{seqno}</span></h1>
        </div>
      </div>
      {
        isLoading ?
          <div className="loading-container">
            <CircularProgress />
            <p>Loading order details...</p>
          </div>
          :
          <Fragment>

            <div className="border rounded p-3 order-item mb-4" style={{ background: bg_color }}>
              {/* ...existing order details header code... */}

              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <table className="order-index-lines">
                  <thead>
                    <tr>
                      <th>STOCKCODE</th>
                      <th>DESCRIPTION</th>
                      <th className="text-end">QTY</th>
                      <th className="text-end">AMOUNT</th>
                    </tr>
                  </thead>

                  <tbody>
                    {
                      order.LINES?.map((line) => (
                        <tr key={line.SEQNO}>
                          <td>{line.STOCKCODE}</td>
                          <td>
                            {line.DESCRIPTION}
                            {line.STOCKCODE === 'WS-H-STC-ASSIGNED' && (
                              <span className="text-success ms-2">
                                (Rebate: -{formatCurrency(Math.floor(line.ORDQUANT) * 35)})
                              </span>
                            )}
                          </td>
                          <td className="text-end">{Math.floor(line.ORDQUANT)}</td>
                          <td className="text-end">
                            {line.STOCKCODE === 'WS-H-STC-ASSIGNED' ?
                              <span className="text-success">-{formatCurrency(Math.floor(line.ORDQUANT) * 35)}</span> :
                              formatCurrency(line.LINETOTAL)
                            }
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="d-block d-md-none">
                <h6 className="mb-3">Order Items:</h6>
                {order.LINES?.map((line) => (
                  <div key={line.SEQNO} className="order-line-mobile-card">
                    <div className="line-item-field">
                      <span className="field-label">Stock Code:</span>
                      <span className="field-value">{line.STOCKCODE}</span>
                    </div>
                    <div className="line-item-field">
                      <span className="field-label">Description:</span>
                      <span className="field-value">
                        {line.DESCRIPTION}
                        {line.STOCKCODE === 'WS-H-STC-ASSIGNED' && (
                          <span className="text-success d-block">
                            (Rebate: -{formatCurrency(Math.floor(line.ORDQUANT) * 35)})
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="line-item-field">
                      <span className="field-label">Quantity:</span>
                      <span className="field-value">{Math.floor(line.ORDQUANT)}</span>
                    </div>
                    <div className="line-item-field">
                      <span className="field-label">Amount:</span>
                      <span className="field-value">
                        {line.STOCKCODE === 'WS-H-STC-ASSIGNED' ?
                          <span className="text-success">-{formatCurrency(Math.floor(line.ORDQUANT) * 35)}</span> :
                          formatCurrency(line.LINETOTAL)
                        }
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Totals */}
              <div className="mt-4">
                <div className="row justify-content-end">
                  <div className="col-12 col-sm-6 col-lg-4">
                    <div className="order-totals-section">
                      <Row className="mb-2">
                        <Col xs={8} className="text-end"><strong>SubTotal:</strong></Col>
                        <Col xs={4} className="text-end">{formatCurrency(order.SUBTOTAL)}</Col>
                      </Row>

                      <Row className="mb-2">
                        <Col xs={8} className="text-end"><strong>GST:</strong></Col>
                        <Col xs={4} className="text-end">{formatCurrency(order.TAXTOTAL)}</Col>
                      </Row>

                      {rebateAmount > 0 && (
                        <Row className="mb-2">
                          <Col xs={8} className="text-end"><strong>STC Rebate:</strong></Col>
                          <Col xs={4} className="text-end text-success">-{formatCurrency(rebateAmount)}</Col>
                        </Row>
                      )}

                      <hr className="my-2" />

                      <Row className="fw-bold">
                        <Col xs={8} className="text-end"><strong>Final Total:</strong></Col>
                        <Col xs={4} className="text-end">{formatCurrency(finalTotal)}</Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <Link to="/orders/paginated" className="btn btn-outline-primary">
                  <span>← Back to Order Index</span>
                </Link>
              </div>
            </div>

            {!reorder && <DocumentUpload seqno={seqno} />}

          </Fragment>
      }

      <br /><br /><br /><br /><br />
    </Container>
  );
}